import { clsx } from 'clsx';
import { XMarkIcon } from '@heroicons/react/24/outline';
import type { BulkConversionProgressProps } from './MasterDocument.types';
import { useMasterDocumentTranslation } from './i18n';
import { inlineActionButtonDanger } from './inlineActions';

function SpinnerIcon({ className }: { className?: string }) {
  return (
    <svg
      className={clsx('animate-spin', className)}
      xmlns="http://www.w3.org/2000/svg"
      fill="none"
      viewBox="0 0 24 24"
      aria-hidden="true"
    >
      <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
      <path
        className="opacity-75"
        fill="currentColor"
        d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z"
      />
    </svg>
  );
}

function CheckCircleIcon({ className }: { className?: string }) {
  return (
    <svg className={className} xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2} aria-hidden="true">
      <path strokeLinecap="round" strokeLinejoin="round" d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
    </svg>
  );
}

function XCircleIcon({ className }: { className?: string }) {
  return (
    <svg className={className} xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2} aria-hidden="true">
      <path strokeLinecap="round" strokeLinejoin="round" d="M10 14l2-2m0 0l2-2m-2 2l-2-2m2 2l2 2m7-2a9 9 0 11-18 0 9 9 0 0118 0z" />
    </svg>
  );
}

export function BulkConversionProgress({
  job,
  onCancel,
  onClose,
  onViewResults,
  className,
}: BulkConversionProgressProps) {
  const { t } = useMasterDocumentTranslation();
  const { status, total, processed, results } = job;

  const failedCount = results.filter((r) => r.status === 'failed').length;
  const partialCount = results.filter((r) => r.status === 'partial').length;
  const pct = total > 0 ? (processed / total) * 100 : 0;
  const isActive = status === 'running' || status === 'pending';
  const isTerminal = status === 'completed' || status === 'failed' || status === 'cancelled';

  // "Completado — 2 errores, 1 parcial": son DOS contadores independientes en una
  // sola frase y `count` solo resuelve un plural, así que cada fragmento se
  // pluraliza por separado y se inyecta como `{detail}`.
  const conErrores = t('master_document.crossing.bulkErrorCount', { count: failedCount });
  const conParciales = t('master_document.crossing.bulkPartialCount', { count: partialCount });
  const detalleCompletado =
    failedCount > 0 && partialCount > 0
      ? `${conErrores}, ${conParciales}`
      : failedCount > 0
        ? conErrores
        : conParciales;

  return (
    <div
      className={clsx(
        'fixed bottom-5 right-5 z-[200] w-80',
        'bg-bg-primary dark:bg-dark-bg-primary',
        'rounded-2xl',
        'shadow-[0_8px_32px_rgba(0,0,0,.18)] dark:shadow-[0_8px_32px_rgba(0,0,0,.5)]',
        'border border-border-primary dark:border-dark-border-primary',
        'overflow-hidden',
        className,
      )}
      role="status"
      aria-live="polite"
    >
      {/* Header */}
      <div className="flex items-center gap-2 px-4 py-3 bg-background-secondary dark:bg-dark-bg-secondary">
        {isActive && (
          <SpinnerIcon className="w-4 h-4 shrink-0 text-primary-custom-600" />
        )}
        {status === 'completed' && (
          <CheckCircleIcon className="w-4 h-4 shrink-0 text-green-600" />
        )}
        {status === 'failed' && (
          <XCircleIcon className="w-4 h-4 shrink-0 text-red-600" />
        )}
        {status === 'cancelled' && (
          <XCircleIcon className="w-4 h-4 shrink-0 text-zinc-400" />
        )}
        <span className="flex-1 text-sm font-bold text-content-primary dark:text-dark-content-primary">
          {t('master_document.crossing.bulkTitle')}
        </span>
        {isTerminal && (
          <button
            type="button"
            onClick={onClose}
            className={clsx(
              'p-1 rounded-md transition-colors',
              'text-content-secondary hover:bg-border-primary',
              'dark:text-dark-content-secondary dark:hover:bg-dark-bg-primary',
              'focus:outline-hidden focus:ring-2 focus:ring-primary-custom-400 focus:ring-offset-2',
              'dark:focus:ring-dark-border-custom dark:focus:ring-offset-dark-bg-primary',
            )}
            aria-label={t('master_document.chrome.close')}
          >
            <XMarkIcon className="w-4 h-4" aria-hidden="true" />
          </button>
        )}
      </div>

      {/* Body */}
      <div className="px-4 py-3 space-y-3">
        {/* Progress bar */}
        <div className="space-y-1.5">
          <div className="flex justify-between text-xs text-content-secondary dark:text-dark-content-secondary">
            <span>{t('master_document.crossing.bulkProgress')}</span>
            <span className="tabular-nums">{processed} / {total}</span>
          </div>
          <div className="w-full h-2 rounded-full bg-border-primary dark:bg-dark-border-primary overflow-hidden">
            <div
              className={clsx(
                'h-full rounded-full transition-all duration-500',
                status === 'completed' ? 'bg-green-500' :
                status === 'failed' ? 'bg-red-500' :
                status === 'cancelled' ? 'bg-zinc-400' : 'bg-primary-custom-500',
              )}
              style={{ width: `${pct}%` }}
            />
          </div>
        </div>

        {/* Status message */}
        {isActive && (
          <p className="text-xs text-content-secondary dark:text-dark-content-secondary">
            {t('master_document.crossing.bulkBackgroundNote')}
          </p>
        )}
        {status === 'completed' && (
          <p className={clsx('text-xs font-semibold', failedCount > 0 ? 'text-red-600 dark:text-red-400' : partialCount > 0 ? 'text-amber-600 dark:text-amber-400' : 'text-green-600 dark:text-green-400')}>
            {failedCount > 0 || partialCount > 0
              ? t('master_document.crossing.bulkCompletedWith', { detail: detalleCompletado })
              : t('master_document.crossing.bulkCompleted')}
          </p>
        )}
        {status === 'failed' && (
          <p className="text-xs font-semibold text-red-600 dark:text-red-400">
            {t('master_document.crossing.bulkFailed')}
          </p>
        )}
        {status === 'cancelled' && (
          <p className="text-xs text-content-secondary dark:text-dark-content-secondary">
            {t('master_document.crossing.bulkCancelled', { count: processed })}
          </p>
        )}

        {/* Actions */}
        <div className="flex items-center justify-end gap-2 pt-1">
          {isActive && (
            <button
              type="button"
              onClick={onCancel}
              className={inlineActionButtonDanger}
            >
              {t('master_document.chrome.cancel')}
            </button>
          )}
          {isTerminal && results.length > 0 && (
            <button
              type="button"
              onClick={onViewResults}
              className={clsx(
                'px-3 py-1.5 rounded-md text-xs font-semibold transition-colors',
                'bg-primary-inverse-background text-primary-inverse-content hover:bg-primary-custom-700',
                'dark:bg-dark-bg-inverse dark:text-dark-content-inverse dark:hover:bg-dark-bg-inverse/90',
                'focus:outline-hidden focus:ring-2 focus:ring-primary-custom-400 focus:ring-offset-2',
                'dark:focus:ring-dark-border-custom dark:focus:ring-offset-dark-bg-primary',
              )}
            >
              {t('master_document.crossing.bulkViewResults')}
            </button>
          )}
        </div>
      </div>
    </div>
  );
}
